/**
 * Team Profile Types
 *
 * Sub-user model that sits on top of a single vendor Firebase Auth account.
 * The vendor (e.g., a wedding venue) keeps one Firebase Auth user; each office
 * staff member ("Sara", "David", the manager) gets a TeamProfile. After login,
 * the device picks a profile (Netflix-style). Actions are tagged with the active
 * profileId for internal attribution. Customers never see profile names — they
 * always see the vendor's businessName.
 *
 * Storage:  serviceProviders/{vendorId}/profiles/{profileId}
 */

import type { Timestamp } from "firebase/firestore";

/**
 * Roles. Only two — Manager + Team Member. "Right hand" is just a Team Member
 * with every capability flipped on (achieved via Grant All).
 */
export type TeamProfileRole = "manager" | "team_member";

/**
 * Per-Team-Member capability toggles. Manager always has everything implicitly —
 * these flags only gate Team Member access. New Team Members start with every
 * flag false; the Manager grants what they trust.
 *
 * NOT toggleable (Manager-only forever, no flag here):
 *  - Manager PIN management
 *  - Manager role transfer (promote/demote to manager)
 *
 * Team & Permissions management itself is now a toggleable capability
 * (`manageTeam`) so a Manager can DELEGATE add/edit of team members + their
 * permissions — but role transfer and the PIN stay Manager-only even for a
 * delegate (those are still gated by useIsManager, not manageTeam).
 *
 * Always available to all profiles (no flag — bare minimum to function or
 * deliberately ungated):
 *  - Messages with customers
 *  - Event Mode on/off toggle
 *  - AI cost tracking view
 */
export interface ProfilePermissions {
  viewFinancials: boolean;
  editPublicProfile: boolean;
  editPricing: boolean;
  sendQuotes: boolean;
  sendContracts: boolean;
  /** View the calendar. Default ON for team members (preserves open access). */
  viewCalendar: boolean;
  /** Add/edit calendar events. Default OFF. Viewing is gated by viewCalendar. */
  editCalendar: boolean;
  /** Manage off-platform integrations (email, ads, hosted form, Green Invoice). */
  manageIntegrations: boolean;
  /** Add/edit team members + their permissions. Role transfer + PIN stay
   *  Manager-only (useIsManager), even with this granted. */
  manageTeam: boolean;
  /** Access the leads / pipeline area. Default ON (preserves open access). */
  accessLeads: boolean;
}

/**
 * Capability keys — string literal union derived from ProfilePermissions.
 * Used by useHasPermission(capability) so callers get autocomplete + type safety.
 */
export type Capability = keyof ProfilePermissions;

/**
 * List of every capability (handy for iterating in the Team Management UI).
 * Keep this in sync with ProfilePermissions.
 */
export const ALL_CAPABILITIES: Capability[] = [
  "viewFinancials",
  "editPublicProfile",
  "editPricing",
  "sendQuotes",
  "sendContracts",
  "viewCalendar",
  "editCalendar",
  "manageIntegrations",
  "manageTeam",
  "accessLeads",
];

/**
 * Default permissions for a brand-new Team Member. Most flags off; `viewCalendar`
 * and `accessLeads` default ON to preserve the historically-open access to the
 * calendar and the leads/pipeline area (managers can restrict per-member).
 * Manager grants the rest. Use grantAllPermissions() to flip everything on
 * (the "right hand" shortcut).
 */
export const DEFAULT_TEAM_MEMBER_PERMISSIONS: ProfilePermissions = {
  viewFinancials: false,
  editPublicProfile: false,
  editPricing: false,
  sendQuotes: false,
  sendContracts: false,
  viewCalendar: true,
  editCalendar: false,
  manageIntegrations: false,
  manageTeam: false,
  accessLeads: true,
};

/**
 * Manager permissions — every flag on. Mirrors what Grant All produces for a
 * Team Member, but is stored on the Manager doc to keep permission lookups
 * uniform regardless of role.
 */
export const MANAGER_PERMISSIONS: ProfilePermissions = {
  viewFinancials: true,
  editPublicProfile: true,
  editPricing: true,
  sendQuotes: true,
  sendContracts: true,
  viewCalendar: true,
  editCalendar: true,
  manageIntegrations: true,
  manageTeam: true,
  accessLeads: true,
};

/**
 * Profile avatar. Each profile is either a colored circle with white initials
 * (the default) or a photo the person uploaded.
 *
 * - `color` is always present — it is the fallback shown wherever no photo is
 *   set, and stays around so removing a photo reverts to the last chosen color.
 * - `photoUrl` (optional) is a Firebase Storage download URL. When set, every
 *   render site shows the image instead of the color+initials.
 * - `photoStoragePath` (optional) is the Storage object path backing
 *   `photoUrl`, kept so a later upload/removal can delete the old object.
 *
 * presetId reserved for future preset shapes (always "default" today).
 */
export interface ProfileAvatar {
  presetId: string;
  color: string;
  photoUrl?: string;
  photoStoragePath?: string;
}

/**
 * Avatar color palette — picker chooses one of these when creating a profile.
 * Subdued tones that read well in both light and dark UI.
 */
export const AVATAR_COLORS: string[] = [
  "#B063E0",
  "#7B4FE0",
  "#4F8DE0",
  "#4FCFE0",
  "#4FE0A8",
  "#A8E04F",
  "#E0CF4F",
  "#E0954F",
  "#E04F7B",
  "#E04FCF",
  "#6B7280",
  "#111827",
];

/**
 * A team profile under a vendor account.
 *
 * Firestore path: serviceProviders/{vendorId}/profiles/{id}
 * - vendorId is implicit in the path (the parent doc).
 * - pinHash and managerPhone exist only when role === "manager".
 */
export interface TeamProfile {
  id: string;
  name: string;
  avatar: ProfileAvatar;
  role: TeamProfileRole;
  permissions: ProfilePermissions;

  // Manager-only fields. Absent on team_member profiles.
  pinHash?: string;
  managerPhone?: string;

  // For the picker's "Active 2h ago" indicator (manager-visible only).
  lastActiveAt?: Timestamp;

  // Per-profile work email — the address this profile sends customer
  // replies from in their own mail client. Used by the outbound-mail
  // capture pipeline to attach this profile's sent messages back to the
  // lead timeline. OAuth tokens live separately under
  // `profiles/{profileId}/private/gmailTokens` (or `outlookTokens`); this
  // is the user-visible setting + the matching identifier when a poller
  // reconciles a sent item to a profile.
  workEmail?: string;

  // Multi-manager owner tag. Set ONLY on the auto-created "Owner" profile
  // belonging to a multi-manager CEO who entered this venue via
  // signInWithCustomToken. Used by:
  //  - findOrCreateOwnerProfile() to locate the CEO's profile inside any
  //    given venue and auto-select it after the auth swap (so the CEO never
  //    sees the team-profile picker — their seat is reserved).
  //  - the profile picker UI to know which profile cards are "external"
  //    (owner of the parent organization) vs. on-site staff.
  // PIN-locked at creation with an unguessable hash so workers logging in
  // directly as the venue can see the card but cannot enter it; the CEO
  // bypasses the PIN UI because their session sets the profile active
  // automatically post-swap.
  primaryUid?: string;

  // ─── Cross-venue "seats" (Layer 2) ──────────────────────────────────
  // A person can hold a profile ("seat") in more than one venue of the same
  // business group. Same-person-ness is NEVER inferred (names collide) — it is
  // DECLARED when a seat is granted/adopted, and carried by a shared personKey.
  //
  // All four fields below are SERVER-WRITTEN ONLY (locked in firestore.rules
  // like pinHash) — a client write would be cross-venue identity spoofing.

  /** Stable identity shared by one person's profiles across venues. Minted =
   *  the person's HOME profile doc id. Two profiles in two venues with the same
   *  personKey are the same human. Absent on ordinary single-venue profiles. */
  personKey?: string;

  /** Set ONLY on a profile that exists purely as a granted seat (created by the
   *  seat flow in a venue that is NOT this person's home). Its presence means
   *  "managed from {homeVendorId}" — such a profile is not locally
   *  renameable/deletable and can't itself sprout further seats. Adopted local
   *  profiles do NOT get this (they are legitimately local staff). */
  homeVendorId?: string;

  /** On the HOME profile: the venues this person has been granted a seat in.
   *  The AUTHORITATIVE grant list (enterVenue re-checks the counterpart, but
   *  this drives the picker/chip UI). Server-maintained by setVenueSeat. */
  seatVendorIds?: string[];

  /** On a CREATED seat counterpart: true when the grant was revoked. The doc +
   *  its stats are KEPT (Yuval: re-granting must return the person to exactly
   *  where they were), but a disabled seat is hidden from pickers, team lists,
   *  and per-profile breakdowns, and can't be switched into. */
  seatDisabled?: boolean;

  createdAt: Timestamp;
  createdBy: string;
}

/**
 * Payload for creating a new profile. id, createdAt, createdBy filled by the service.
 */
export interface CreateProfilePayload {
  name: string;
  avatar: ProfileAvatar;
  role: TeamProfileRole;
}

/**
 * Payload for updating a profile (name, avatar). Use the dedicated grant/revoke
 * and PIN helpers in teamProfileService for permission and PIN changes —
 * those have their own validation rules.
 */
export interface UpdateProfilePayload {
  name?: string;
  avatar?: ProfileAvatar;
}

/**
 * Result of attempting PIN verification.
 */
export type PinVerifyResult = "ok" | "wrong" | "no_pin_set";
